import { useEffect, useRef, useState, useTransition } from 'react';
import { useTranslations } from '@/i18n/translations';
import {
  Body, Button, Eyebrow, Heading, I, Icon, Dialog, DialogFooter, DialogSection, Label, Row, SkeletonText, Stack, Textarea, } from '@leanin/ui';
import { toast } from '@leanin/ui/client';
import type { IntakeFormKind } from '@leanin/shared/intake';
import { MAX_ANSWER_CHARS } from '@leanin/shared/intake';
import { useApplicantIntakeForm } from '@/lib/queries/groups';
import { trackIntakeFormSubmitted } from '@/lib/analytics/events';
import {
  IntakeFormFields,
  type AnswerPayload,
  type AnswerState,
  type ApplicantIntakeForm,
  type ApplicantQuestion,
  type ConsentPayload,
  answerComplete,
  answerTooLong,
  buildAnswers,
  buildConsent,
  classifyIntakeSubmitError,
  isSubmittable,
  mergeAnswers,
  useIntakePrefill,
  useTrackIntakeFormViewed,
} from '@/components/intake';
import styles from './RequestToJoinGroupModal.module.css';

const NOTE_MAX = 1000;

// The submit payload: either the legacy `message` (flag off / no form) or the
// intake fields (formVersionId + answers + consent). The caller forwards it to
// `useJoinGroup`.
export type JoinSubmitInput = {
  message?: string;
  formVersionId?: string;
  answers?: AnswerPayload[];
  consent?: ConsentPayload;
};

export type JoinSubmitResult =
  | { ok: true }
  | { ok: false; code?: string; message: string };

export interface RequestToJoinGroupModalProps {
  open: boolean;
  onClose: () => void;
  groupId: string;
  groupKind: 'circle' | 'network';
  groupName: string;
  onSubmit: (input: JoinSubmitInput) => Promise<JoinSubmitResult>;
  /** Preview mode (leader "Preview form"): render the applicant form from a
   *  provided draft form instead of fetching, with submit disabled. Nothing is
   *  ever submitted — the leader just sees exactly what applicants will. */
  isPreview?: boolean;
  previewForm?: ApplicantIntakeForm | null;
  /** Edit mode (requester revising their own pending request): the form is
   *  seeded from the previously-submitted snapshot, the heading + submit label
   *  reflect editing, and a successful save closes the modal without the
   *  "Request sent" toast. Defaults to the create/join flow. */
  mode?: 'create' | 'edit';
  /** Edit mode only: the requester's prior answers (keyed by questionId) and the
   *  consent statement keys they agreed to, used to pre-fill the form once. */
  initialAnswers?: AnswerState;
  initialConsentKeys?: readonly string[];
}

export function RequestToJoinGroupModal({
  open,
  onClose,
  groupId,
  groupKind,
  groupName,
  onSubmit,
  isPreview = false,
  previewForm = null,
  mode = 'create',
  initialAnswers,
  initialConsentKeys,
}: RequestToJoinGroupModalProps) {
  const t = useTranslations('Group');
  const isEdit = mode === 'edit';
  const formKind: IntakeFormKind =
    groupKind === 'circle' ? 'member_to_circle' : 'member_to_network';
  // Preview renders a provided draft form and never fetches.
  const formQuery = useApplicantIntakeForm(groupId, formKind, {
    enabled: open && !isPreview,
  });
  const form: ApplicantIntakeForm | null = isPreview
    ? previewForm
    : (formQuery.data?.form ?? null);
  // Loading only while open, before the first response.
  const loadingForm =
    !isPreview && open && formQuery.data === undefined && !formQuery.isError;
  // A genuine {form:null} at 200 means "no form applies" → plain note. A fetch
  // ERROR (5xx/network) is distinct: we can't know whether a form is required,
  // so we show a retry state rather than degrading to a note the server may
  // reject. Scoped to the INITIAL load (`data === undefined`): once a form has
  // loaded, a failed RECOVERY refetch — e.g. the stale-version reload after the
  // leader edited the form — must NOT collapse the questions into the dead-end
  // "Try again" state; keep the cached questions rendered under the inline
  // "…just updated…" message so the applicant can review and resend.
  const formLoadError =
    !isPreview && open && formQuery.isError && formQuery.data === undefined;

  const [note, setNote] = useState('');
  const [answers, setAnswers] = useState<AnswerState>({});
  const [agreed, setAgreed] = useState<ReadonlySet<string>>(new Set());
  const [touched, setTouched] = useState<ReadonlySet<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();
  // Dismissal guard for the submit flight. Set synchronously in the submit
  // handler, before the transition — `isPending` is render state, and an
  // Escape keydown can land before its pending render commits; dismissing
  // then runs the close-reset effect and wipes every answer mid-submit.
  const submitInFlightRef = useRef(false);
  // Edit mode seeds the form from the saved snapshot exactly once per open — a
  // stale-version refetch (or any re-render) must not clobber in-progress edits.
  const editSeededRef = useRef(false);

  useEffect(() => {
    if (!open) {
      setNote('');
      setAnswers({});
      setAgreed(new Set());
      setTouched(new Set());
      setError(null);
      editSeededRef.current = false;
    }
  }, [open]);

  // (Re)seed answers whenever the form version changes — first load or a
  // stale-version refetch — preserving answers whose questionId still exists.
  // `prefill` seeds recommended region/sector questions from the profile; it
  // is stable once the profile resolves, so a late resolve re-seeds only the
  // untouched, still-blank prefill fields (touched/non-empty answers are
  // preserved). `touched` is read via closure, not a dep, so a blur doesn't
  // re-seed — the re-seed rides the formVersionId/prefill transitions.
  const prefill = useIntakePrefill();
  const formVersionId = form?.formVersionId;
  // Re-seed on the open transition too: the modal stays mounted, so reopening
  // with an already-cached form keeps the same formVersionId — without `open`
  // in the deps the seed never re-runs and prefilled region/sector answers
  // (cleared on the previous close) render blank on the second open.
  useEffect(() => {
    if (open && form !== null) {
      if (isEdit && !editSeededRef.current) {
        // First seed of an edit session: the requester's saved answers win over
        // profile prefill (mergeAnswers preserves `base` values by questionId,
        // dropping any question the form has since removed), and the consents
        // they previously agreed to are pre-checked.
        editSeededRef.current = true;
        setAnswers(mergeAnswers(form, { ...(initialAnswers ?? {}) }, prefill, touched));
        setAgreed(new Set(initialConsentKeys ?? []));
      } else {
        setAnswers((prev) => mergeAnswers(form, prev, prefill, touched));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on open/formVersionId + prefill; `form`/`touched`/`isEdit`/`initial*` read via closure
  }, [open, formVersionId, prefill]);

  useTrackIntakeFormViewed(form, formKind, open && !isPreview);

  const submittable = form !== null ? isSubmittable(form, answers, agreed) : true;

  // Inline errors reveal once a field has been touched (blurred). With
  // submit-disabled-until-complete, the disabled button + the live remaining
  // count carry the "not ready" signal; per-field errors just pinpoint a
  // touched-but-incomplete field.
  const errorFor = (q: ApplicantQuestion): string | null => {
    if (!touched.has(q.id)) return null;
    if (answerTooLong(answers[q.id])) {
      return t('intakeForm.errors.answerTooLong', { max: MAX_ANSWER_CHARS });
    }
    if (q.required && !answerComplete(q, answers[q.id])) {
      return t('intakeForm.errors.answerRequired');
    }
    return null;
  };

  const submit = () => {
    if (isPreview) return; // Preview never submits.
    if (submitInFlightRef.current) return;
    submitInFlightRef.current = true;
    setError(null);
    startTransition(async () => {
      try {
        let payload: JoinSubmitInput;
        if (form !== null) {
          // Send is disabled until submittable; this guard is defensive.
          if (!submittable) return;
          payload = {
            formVersionId: form.formVersionId,
            answers: buildAnswers(form, answers),
            consent: buildConsent(agreed),
          };
        } else {
          const message = note.trim();
          payload = message.length > 0 ? { message } : {};
        }

        const result = await onSubmit(payload);
        if (result.ok) {
          if (form !== null) {
            trackIntakeFormSubmitted({
              form_kind: formKind,
              form_version: form.versionNumber,
              question_count: form.questions.length,
            });
          }
          // Editing an existing request has no "sent" milestone — the refreshed
          // Pending Requests / review modal reflects the new answers.
          if (!isEdit) {
            toast.success(t('join.sent.title'), {
              description: t('join.sent.description'),
            });
          }
          onClose();
          return;
        }

        const kind = classifyIntakeSubmitError(result.code);
        if (kind === 'stale_version') {
          // The leader edited the form after it loaded. Pull the new version
          // (answers are preserved by questionId) and ask the applicant to review.
          void formQuery.refetch();
          setError(t('intakeForm.submitErrors.stale_version'));
          return;
        }
        setError(
          kind === 'required'
            ? t('intakeForm.submitErrors.required')
            : kind === 'too_large'
              ? t('intakeForm.submitErrors.too_large')
              : kind === 'malformed'
                ? t('intakeForm.submitErrors.malformed')
                : result.message,
        );
      } finally {
        submitInFlightRef.current = false;
      }
    });
  };

  const sendDisabled =
    isPreview || loadingForm || formLoadError || (form !== null && !submittable);

  // In-modal close buttons are programmatic closes — `canDismiss` only gates
  // Escape / outside click — and their `disabled={isPending}` shares the
  // stale-render window, so they check the ref themselves.
  const requestClose = () => {
    if (submitInFlightRef.current) return;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => { if (!v) onClose(); }}
      canDismiss={() => !submitInFlightRef.current && !isPending}
      size="lg"
      accessibleTitle={t('join.accessibleTitle', { groupName })}
      className={styles.modal}
    >
      <DialogSection bleed>
        <Row
          justify="space-between"
          align="flex-start"
          gap="lg"
          className={styles.header}
        >
          <Stack gap="sm" style={{ minWidth: 0 }}>
            {isPreview && (
              <Eyebrow tone="default">{t('join.previewEyebrow')}</Eyebrow>
            )}
            <Heading level={3} className={styles.title}>
              {isEdit
                ? t('join.editHeading', { groupName })
                : t('join.heading', { groupName })}
            </Heading>
            <Body color="mute" className={styles.subtitle}>
              {t.rich('join.subtitle', {
                b: (chunks) => <strong>{chunks}</strong>,
                kind: groupKind,
                groupName,
              })}
            </Body>
          </Stack>
          <Button
            variant="ghost"
            size="sm"
            icon={I.x}
            aria-label={t('join.close')}
            onClick={requestClose}
            disabled={isPending}
          />
        </Row>
      </DialogSection>
      <DialogSection scrollable bleed>
        <Stack gap="lg" className={styles.body}>
          {loadingForm ? (
            <SkeletonText lines={4} size="md" />
          ) : formLoadError ? (
            <Stack gap="md">
              <Body size="sm" color="destructive">
                {t('intakeForm.loadError')}
              </Body>
              <Row>
                <Button
                  variant="secondary"
                  size="md"
                  onClick={() => {
                    void formQuery.refetch();
                  }}
                >
                  {t('intakeForm.retry')}
                </Button>
              </Row>
            </Stack>
          ) : form !== null ? (
            <Stack gap="md">
              <IntakeFormFields
                form={form}
                answers={answers}
                agreed={agreed}
                onAnswerChange={(id, value) => {
                  setAnswers((p) => ({ ...p, [id]: value }));
                  setError(null);
                }}
                onAnswerBlur={(id) => setTouched((p) => new Set(p).add(id))}
                onConsentToggle={(key, next) =>
                  setAgreed((p) => {
                    const s = new Set(p);
                    if (next) s.add(key);
                    else s.delete(key);
                    return s;
                  })
                }
                errorFor={errorFor}
                consentError={null}
              />
            </Stack>
          ) : (
            <Stack gap="sm">
              <Row justify="space-between" align="baseline">
                <Label size="lg">{t('join.noteLabel')}</Label>
                <Body size="sm" color="mute">
                  {t('join.optional')}
                </Body>
              </Row>
              <Textarea
                rows={4}
                value={note}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v.length <= NOTE_MAX) setNote(v);
                }}
                placeholder={t('join.notePlaceholder', { kind: groupKind })}
              />
              <Row justify="space-between" align="center" gap="sm">
                <Row gap="sm" align="center" style={{ minWidth: 0 }}>
                  <Icon size={14} color="var(--muted-foreground)">
                    {I.lock}
                  </Icon>
                  <Body size="sm" color="mute">
                    {t('join.messageVisibleTo', { kind: groupKind })}
                  </Body>
                </Row>
                <Body size="sm" color="mute">
                  {t('join.charCount', { count: note.length, max: NOTE_MAX })}
                </Body>
              </Row>
            </Stack>
          )}

          {error !== null && (
            <span role="alert" aria-live="polite">
              <Body size="sm" color="destructive">
                {error}
              </Body>
            </span>
          )}
        </Stack>
      </DialogSection>
      <DialogFooter className={styles.footer}>
        <Row
          justify="flex-end"
          align="center"
          wrap
          gap="md"
          style={{ flex: 1, minWidth: 0 }}
        >
          <Button variant="ghost" size="lg" onClick={requestClose} disabled={isPending}>
            {t('join.cancel')}
          </Button>
          <Button
            variant="primary"
            size="lg"
            iconRight={isEdit ? I.check : I.send}
            onClick={submit}
            loading={isPending}
            disabled={sendDisabled}
          >
            {isEdit ? t('join.saveChanges') : t('join.sendRequest')}
          </Button>
        </Row>
      </DialogFooter>
    </Dialog>
  );
}
